<template>
  <div class="date-time-picker">

    <date-picker
      :value="internalDateObject"
      :popover="popover"
      :default-date="dateNow"
      @input="emitDateInput"
    >
      <template v-slot="{ inputEvents, hidePopover }">
        <div
          class="input-group time-input-group"
          v-on="popoverTriggerEvents(inputEvents)"
          @click="onPickerActivate(hidePopover)"
        >
          <input
            ref="dateInput"
            :name="fieldName"
            :disabled="disable"
            type="text"
            :value="showingValue"
            class="form-control"
            @keyup="typing=true"
            @focusout="emitDateStringInput"
            @paste="emitDateStringInput"
          />
          <span class="input-group-addon">
            <span class="glyphicon glyphicon-calendar"></span>
          </span>
        </div>
      </template>
    </date-picker>
  </div>
</template>

<script>
import DatePicker from "v-calendar/lib/components/date-picker.umd.js";
import { getEventValue, transformDate } from "src/lib/editor/utils.js";

// Coordinates open date pickers across all DateTimeSelector instances so that
// only one calendar is visible at a time. Each picker registers a listener and,
// when any picker is activated, every other open picker hides its own popover.
// v-calendar uses a separate popover per picker (visibility 'click' with
// autoHide disabled), so without this two calendars can stay open at once.
const openPickerListeners = new Set();
let pickerIdCounter = 0;

function notifyPickerOpened(openedId) {
  openPickerListeners.forEach((listener) => listener(openedId));
}

export default {
  name: "DateTimeSelector",
  components: {
    "date-picker": DatePicker,
  },
  props: {
    // The selected visible date
    value: {
      type: String,
      default: () => transformDate(new Date()),
    },
    disable: {
        type: Boolean,
        default: false,
    },
    // The color of the selected values
    color: {
      type: String,
      default: null,
    },
    // Used to handle YYYY-MM-DDThh:mm[:ss[.uuuuuu]][+HH:MM|-HH:MM|Z] format
    isDateTimeField: {
      type: Boolean,
      default: false,
    },
    // Name of this field
    fieldName: {
        type: String,
        default: ''
    }
  },
  data: () => ({
    dateNow: new Date(),
    showingValue: "",
    typing: false,
  }),
  computed: {
    internalDateObject: function() {
      if (this.value) {
        return new Date(this.value);
      } else {
        return new Date();
      }
    },
    popover: function() {
        return {placement: 'bottom', visibility: this.disable ? 'hidden' : 'click'}
    }
  },
  created: function () {
    if (this.value) {
      this.showingValue = this.value.substring(0, 10);
    }
    // Unique id + listener used to close this picker when another one opens.
    this.pickerId = ++pickerIdCounter;
    this.hidePopoverFn = null;
    this.onOtherPickerOpen = (openedId) => {
      if (openedId !== this.pickerId && this.hidePopoverFn) {
        this.hidePopoverFn();
      }
    };
    openPickerListeners.add(this.onOtherPickerOpen);
  },
  beforeDestroy: function () {
    openPickerListeners.delete(this.onOtherPickerOpen);
  },
  methods: {
    // Called when this picker's input is clicked (which opens its calendar).
    // We remember how to close this picker's popover, then tell every other
    // picker to close, leaving only this calendar open.
    onPickerActivate: function (hidePopover) {
      this.hidePopoverFn = hidePopover;
      notifyPickerOpened(this.pickerId);
    },
    // v-calendar's scoped-slot `inputEvents` bundle both the popover triggers
    // (click/hover/focus) AND its own text-parsing handlers (`input`, `change`,
    // `keyup`). The `input` handler is debounced by `inputDebounce` (default
    // 1000ms) and clears the field when it can't validate a partial/edited
    // value. Since this component does its own parsing (emitDateStringInput),
    // we bind only the popover triggers and drop v-calendar's text handlers.
    // This restores in-place text editing and removes the 1-2s delete lag.
    popoverTriggerEvents: function (inputEvents) {
      // eslint-disable-next-line no-unused-vars
      const { input, change, keyup, ...triggerEvents } = inputEvents;
      return triggerEvents;
    },
    // Re-sync the displayed text with showingValue without remounting the
    // input. Previously a :key="reRenderKey" bump forced a remount to reset the
    // field after a rejected edit, but remounting detaches the DOM node that
    // v-calendar's popover (popper.js) is anchored to, causing the calendar to
    // jump to the top-left corner (a detached reference reports a 0,0 rect).
    // Writing the value directly keeps the same node, so the anchor survives.
    syncInputDisplay: function () {
      this.$nextTick(() => {
        if (this.$refs.dateInput) {
          this.$refs.dateInput.value = this.showingValue == null ? "" : this.showingValue;
        }
      });
    },
    emitDateInput: function (event) {
      if (!event) {
        this.$emit("input", null);
        this.showingValue = null;
        return;
      }
      let transformedDate = transformDate(event, this.isDateTimeField);
      let date = transformedDate;
      this.showingValue = date.substring(0, 10)
      this.$emit("input", transformedDate);
    },
    emitDateStringInput: function (event) {
        if (this.typing || (!this.typing && event.clipboardData)) {
            let value = this.typing ? getEventValue(event) : event.clipboardData.getData('text')
            let date = new Date(value);
            if (value === "" || (date != "Invalid Date" && value.length === 10)) {
                this.showingValue = value
                if (value != "") {
                    this.$emit("input", transformDate(date, this.isDateTimeField));
                } else {
                    this.$emit("input", null);
                }
                this.syncInputDisplay()
            } else {
                if (this.value) {
                    this.showingValue = this.value.substring(0, 10)
                    this.syncInputDisplay()
                    this.$emit("input", this.showingValue)
                } else {
                    this.showingValue = null
                    this.syncInputDisplay()
                    this.$emit("input", null)
                }
            }
            this.typing = false
        }
    },
  },
};
</script>

<style>
.date-time-picker > span > div.vc-popover-content-wrapper {
  z-index: 1100;
}

.date-time-picker .vc-title {
  color: black;
  font-size: 100%;
}
.date-time-picker .vc-container {
  font-family: Helvetica, "Helvetica Neue", sans-serif;
}

.date-time-picker .vc-weekday {
  color: black;
}

.date-time-picker .vc-border {
  border-width: 1px;
  border-radius: 4px;
  border-color: darkgray;
}

.date-time-picker .vc-svg-icon {
  /*
  These need to be important because v-calendar puts a data attribute on the class selector
  which makes it more specific
   */
  stroke: black !important;
  stroke-width: 3 !important;
}

.date-time-picker .vc-rounded-full {
  border-radius: 1px;
}
</style>
